import React from 'react';
import { TableCell } from '@/components/ui/table';
import type { BotUniqueUrlsFlags } from './BotUniqueUrlsCell';

/**
 * The requests-per-URL cell of the bot crawl table, qualified PER ROW (codex rounds 1 to 4 on
 * #5941): requests divided by a lower-bound count (a chunk peak or a capped set) is an UPPER bound
 * on the true ratio; divided by a HyperLogLog estimate it inherits the estimate and can sit on
 * either side of the truth, so it must not be shown as a bound; a count that is part floor, part
 * estimate is "about at most". The cell computes and ROUNDS the ratio itself: a bound rounds UP at
 * the displayed precision (10 / 3 = 3.333… reads "≤ 3.4x", never "≤ 3.3x"), an approximate value
 * rounds to nearest.
 */
function roundUp1(r: number): string { return (Math.ceil(r * 10 - 1e-9) / 10).toFixed(1); }
function roundNearest1(r: number): string { return r.toFixed(1); }

export function describeReqPerUrl(bot: BotUniqueUrlsFlags): { text: string; title: string } {
  // no unique-URL count: the ratio is unavailable, and zero is not an upper bound (codex round 5)
  if (!(bot.unique_urls > 0)) {
    return {
      text: '—',
      title: 'Unavailable: no unique-URL count was reported for this bot in the window, so requests per URL '
        + 'cannot be computed.',
    };
  }
  const r = bot.requests / bot.unique_urls;
  const partEstimated = bot.unique_urls_estimate
    && (bot.unique_urls_saturated || (bot.unique_urls_capped_part || 0) > 0 || (bot.unique_urls_peak_part || 0) > 0);
  if (partEstimated) {
    return {
      text: `≲ ${roundUp1(r)}x`,
      title: 'Requests divided by a count that is part lower bound (capped or chunk-peak days), part HyperLogLog '
        + 'estimate: about at most this, not a guaranteed upper bound, since the estimated part can overshoot. '
        + 'Rounded up at one decimal.',
    };
  }
  if (bot.unique_urls_estimate && bot.unique_urls_scope === 'run') {
    return {
      text: `≲ ${roundUp1(r)}x`,
      title: 'Requests divided by an estimate of a lower bound on unique URLs (the persisted day sketch could not be '
        + 'loaded, so the day carries one run\'s estimate): about at most this, not a guaranteed upper bound. '
        + 'Rounded up at one decimal.',
    };
  }
  if (bot.unique_urls_estimate) {
    return {
      text: `≈ ${roundNearest1(r)}x`,
      title: 'Requests divided by a HyperLogLog estimate of unique URLs; the ratio inherits the estimate\'s error '
        + 'and can sit on either side of the true ratio.',
    };
  }
  if (bot.unique_urls_saturated) {
    return {
      text: `≤ ${roundUp1(r)}x`,
      title: "Requests divided by a capped count (the capture agent's URL cap was hit), a floor on unique URLs, "
        + 'so the ratio is an upper bound. Rounded up at one decimal.',
    };
  }
  return {
    text: `≤ ${roundUp1(r)}x`,
    title: 'Requests divided by the largest chunk\'s distinct count per day, a lower bound on unique URLs, so the '
      + 'ratio is an upper bound. Rounded up at one decimal.',
  };
}

export function BotReqPerUrlCell({ bot }: { bot: BotUniqueUrlsFlags }) {
  const d = describeReqPerUrl(bot);
  return <TableCell className="text-right text-muted-foreground" title={d.title}>{d.text}</TableCell>;
}
